import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Search,
  ArrowLeft,
  Package,
  DollarSign,
  Clock,
  CheckCircle,
  Truck,
  Filter,
  ChevronDown,
  ChevronUp,
  MapPin,
  CreditCard,
  User,
  Loader2,
  CheckSquare,
  Square,
  X,
  Trash2,
  FlaskConical,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── Types ──

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
  total: number;
  imageUrl?: string;
}

interface OrderPayment {
  method: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  gateway: string;
  gatewayPaymentId?: string;
  amount: number;
  paidAt?: string;
  refundedAmount?: number;
  paymentMode?: 'live' | 'test';
}

type OrderStatus =
  | 'pending_payment'
  | 'confirmed'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

interface OrderAddress {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  items: OrderItem[];
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  total: number;
  currency: string;
  couponCode?: string;
  status: OrderStatus;
  payment: OrderPayment;
  shippingAddress?: OrderAddress;
  billingAddress?: OrderAddress;
  trackingNumber?: string;
  trackingUrl?: string;
  customerNote?: string;
  createdAt: string;
  updatedAt: string;
}

interface OrdersMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface DashboardStats {
  totalProducts: number;
  activeProducts: number;
  totalOrders: number;
  pendingOrders: number;
  paidOrders: number;
  revenue: number;
  lowStockCount: number;
}

// ── Helpers ──

const ORDER_STATUSES: { value: OrderStatus; label: string }[] = [
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

/** Valid lifecycle transitions — matches backend order-service.ts */
const STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending_payment: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled', 'refunded'],
  processing: ['shipped', 'cancelled', 'refunded'],
  shipped: ['delivered', 'refunded'],
  delivered: ['refunded'],
  cancelled: [],
  refunded: [],
};

function getNextStatuses(current: OrderStatus): OrderStatus[] {
  return STATUS_TRANSITIONS[current] || [];
}

function statusLabel(s: OrderStatus): string {
  return ORDER_STATUSES.find((o) => o.value === s)?.label ?? s;
}

function formatCents(cents: number | undefined | null, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format((cents || 0) / 100);
}

function getOrderStatusBadge(status: OrderStatus) {
  const map: Record<OrderStatus, { label: string; className: string }> = {
    pending_payment: {
      label: 'Pending Payment',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    confirmed: {
      label: 'Confirmed',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    processing: {
      label: 'Processing',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    },
    shipped: {
      label: 'Shipped',
      className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    },
    delivered: {
      label: 'Delivered',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    },
    refunded: {
      label: 'Refunded',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
  };
  const entry = map[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

function getPaymentStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    paid: {
      label: 'Paid',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
    refunded: {
      label: 'Refunded',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    },
  };
  const entry = map[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

// ── Component ──

export default function Orders() {
  const { projectId } = useParams<{ projectId: string }>();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<OrderStatus | ''>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  // Fetch orders
  const {
    data: ordersResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['orders', projectId, page, limit, statusFilter, paymentStatusFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (paymentStatusFilter !== 'all') params.paymentStatus = paymentStatusFilter;
      if (search) params.search = search;
      params.sortBy = 'createdAt';
      params.sortOrder = 'desc';
      const response = await api.get(`/projects/${projectId}/ecommerce/orders`, { params });
      return response.data as { success: boolean; data: Order[]; meta: OrdersMeta };
    },
    enabled: Boolean(projectId),
  });

  // Fetch dashboard stats
  const { data: dashboardResponse } = useQuery({
    queryKey: ['ecommerce-dashboard', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/dashboard`);
      return response.data as { success: boolean; data: DashboardStats };
    },
    enabled: Boolean(projectId),
  });

  // Fetch store settings for currency
  const { data: storeSettingsResponse } = useQuery({
    queryKey: ['store-settings', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/settings`);
      return response.data as { success: boolean; data: { currency?: string } };
    },
    enabled: Boolean(projectId),
  });

  const orders = ordersResponse?.data || [];
  const meta = ordersResponse?.meta || { page: 1, limit: 20, total: 0, totalPages: 0 };
  const stats = dashboardResponse?.data;
  const storeCurrency = storeSettingsResponse?.data?.currency || 'INR';

  // ── Single order status mutation ──
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      orderId,
      status,
      note,
    }: {
      orderId: string;
      status: OrderStatus;
      note?: string;
    }) => {
      const response = await api.patch(
        `/projects/${projectId}/ecommerce/orders/${orderId}/status`,
        { status, note }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      toast.success('Order status updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to update status');
    },
  });

  // ── Bulk status mutation ──
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({
      orderIds,
      status,
      note,
    }: {
      orderIds: string[];
      status: OrderStatus;
      note?: string;
    }) => {
      const response = await api.patch(`/projects/${projectId}/ecommerce/orders/bulk-status`, {
        orderIds,
        status,
        note,
      });
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      const data = res?.data;
      if (data?.failed > 0) {
        toast.warning(`${data.succeeded} updated, ${data.failed} failed`);
      } else {
        toast.success(`${data?.succeeded ?? selectedIds.size} orders updated`);
      }
      setSelectedIds(new Set());
      setBulkStatus('');
    },
    onError: () => {
      toast.error('Bulk update failed');
    },
  });

  // ── Bulk delete mutation ──
  const bulkDeleteMutation = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const response = await api.delete(`/projects/${projectId}/ecommerce/orders/bulk-delete`, {
        data: { orderIds },
      });
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      const data = res?.data;
      toast.success(
        `${data?.deleted ?? 0} order${(data?.deleted ?? 0) !== 1 ? 's' : ''} permanently deleted`
      );
      setSelectedIds(new Set());
      setBulkStatus('');
      setShowDeleteConfirm(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to delete orders');
      setShowDeleteConfirm(false);
    },
  });

  // ── Selection helpers ──
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map((o) => o._id)));
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePaymentStatusFilterChange = (value: string) => {
    setPaymentStatusFilter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/projects/${projectId}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ShoppingCart className="h-6 w-6" />
            Orders
          </h1>
          <p className="text-muted-foreground text-sm">
            {meta.total > 0
              ? `${meta.total} order${meta.total !== 1 ? 's' : ''} total`
              : 'Manage your e-commerce orders'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              <Package className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalOrders}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
              <Clock className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingOrders}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid Revenue</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCents(stats.revenue, storeCurrency)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid Orders</CardTitle>
              <CheckCircle className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.paidOrders}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <div className="relative">
                <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Search by order number or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {ORDER_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={paymentStatusFilter} onValueChange={handlePaymentStatusFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  {PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} variant="secondary">
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <Truck className="text-muted-foreground h-8 w-8 animate-pulse" />
                <p className="text-muted-foreground text-sm">Loading orders...</p>
              </div>
            </div>
          ) : isError ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <ShoppingCart className="text-destructive h-8 w-8" />
                <p className="text-destructive text-sm">Failed to load orders</p>
                <p className="text-muted-foreground text-xs">Please try refreshing the page.</p>
              </div>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <Package className="text-muted-foreground h-8 w-8" />
                <p className="text-muted-foreground text-sm">No orders found</p>
                {(search || statusFilter !== 'all' || paymentStatusFilter !== 'all') && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setSearchInput('');
                      setStatusFilter('all');
                      setPaymentStatusFilter('all');
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="bg-muted/50 mb-3 flex items-center gap-3 rounded-lg border px-4 py-3">
                  <span className="text-sm font-medium">{selectedIds.size} selected</span>
                  <Select value={bulkStatus} onValueChange={(v) => setBulkStatus(v as OrderStatus)}>
                    <SelectTrigger className="h-9 w-[180px]">
                      <SelectValue placeholder="Set status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUSES.filter(
                        (s) =>
                          !['cancelled', 'refunded'].includes(s.value) || bulkStatus === s.value
                      ).map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    disabled={!bulkStatus || bulkUpdateMutation.isPending}
                    onClick={() => {
                      if (!bulkStatus) return;
                      bulkUpdateMutation.mutate({
                        orderIds: Array.from(selectedIds),
                        status: bulkStatus as OrderStatus,
                      });
                    }}
                  >
                    {bulkUpdateMutation.isPending ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : null}
                    Apply
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedIds(new Set());
                        setBulkStatus('');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Delete confirmation dialog */}
              <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      Permanently delete {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''}
                      ?
                    </DialogTitle>
                    <DialogDescription>
                      This will permanently delete the selected orders and all related data
                      (reviews, return requests, webhook logs). This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={bulkDeleteMutation.isPending}
                      onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                    >
                      {bulkDeleteMutation.isPending ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-1 h-4 w-4" />
                      )}
                      Delete permanently
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center"
                      >
                        {selectedIds.size === orders.length && orders.length > 0 ? (
                          <CheckSquare className="text-primary h-4 w-4" />
                        ) : (
                          <Square className="text-muted-foreground h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Order #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const isExpanded = expandedOrderId === order._id;
                    const cur = order.currency || storeCurrency;
                    return (
                      <React.Fragment key={order._id}>
                        <TableRow
                          className="hover:bg-muted/50 cursor-pointer"
                          onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => toggleSelect(order._id)}
                              className="flex items-center justify-center"
                            >
                              {selectedIds.has(order._id) ? (
                                <CheckSquare className="text-primary h-4 w-4" />
                              ) : (
                                <Square className="text-muted-foreground h-4 w-4" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="font-medium" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/projects/${projectId}/ecommerce/orders/${order._id}`}
                                className="text-primary hover:underline"
                              >
                                {order.orderNumber}
                              </Link>
                              {order.payment?.paymentMode === 'test' && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-400 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                                >
                                  <FlaskConical className="mr-0.5 h-3 w-3" />
                                  TEST
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{order.customerName}</p>
                              <p className="text-muted-foreground text-xs">{order.customerEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{order.items?.length || 0}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCents(order.total, cur)}
                          </TableCell>
                          <TableCell>{getOrderStatusBadge(order.status)}</TableCell>
                          <TableCell>{getPaymentStatusBadge(order.payment?.status)}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(order.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {/* Expanded order details */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-muted/30 p-0">
                              <div className="space-y-6 p-6">
                                {/* Items */}
                                <div>
                                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                    <Package className="h-4 w-4" /> Items
                                  </h4>
                                  <div className="bg-background rounded-lg border">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="text-muted-foreground border-b">
                                          <th className="p-3 text-left">Product</th>
                                          <th className="p-3 text-center">Qty</th>
                                          <th className="p-3 text-right">Price</th>
                                          <th className="p-3 text-right">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.items?.map((item, idx) => (
                                          <tr key={idx} className="border-b last:border-0">
                                            <td className="flex items-center gap-3 p-3">
                                              {item.imageUrl && (
                                                <img
                                                  src={item.imageUrl}
                                                  alt={item.name}
                                                  className="h-10 w-10 rounded object-cover"
                                                />
                                              )}
                                              <span className="font-medium">{item.name}</span>
                                            </td>
                                            <td className="p-3 text-center">{item.quantity}</td>
                                            <td className="p-3 text-right">
                                              {formatCents(item.price, cur)}
                                            </td>
                                            <td className="p-3 text-right font-medium">
                                              {formatCents(item.total, cur)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="border-t">
                                        <tr>
                                          <td
                                            colSpan={3}
                                            className="text-muted-foreground p-3 text-right"
                                          >
                                            Subtotal
                                          </td>
                                          <td className="p-3 text-right">
                                            {formatCents(order.subtotal, cur)}
                                          </td>
                                        </tr>
                                        {order.discountAmount > 0 && (
                                          <tr>
                                            <td
                                              colSpan={3}
                                              className="text-muted-foreground px-3 pb-1 text-right"
                                            >
                                              Discount
                                              {order.couponCode ? ` (${order.couponCode})` : ''}
                                            </td>
                                            <td className="px-3 pb-1 text-right text-green-600">
                                              -{formatCents(order.discountAmount, cur)}
                                            </td>
                                          </tr>
                                        )}
                                        {order.taxAmount > 0 && (
                                          <tr>
                                            <td
                                              colSpan={3}
                                              className="text-muted-foreground px-3 pb-1 text-right"
                                            >
                                              Tax
                                            </td>
                                            <td className="px-3 pb-1 text-right">
                                              {formatCents(order.taxAmount, cur)}
                                            </td>
                                          </tr>
                                        )}
                                        {order.shippingAmount > 0 && (
                                          <tr>
                                            <td
                                              colSpan={3}
                                              className="text-muted-foreground px-3 pb-1 text-right"
                                            >
                                              Shipping
                                            </td>
                                            <td className="px-3 pb-1 text-right">
                                              {formatCents(order.shippingAmount, cur)}
                                            </td>
                                          </tr>
                                        )}
                                        <tr className="border-t font-semibold">
                                          <td colSpan={3} className="p-3 text-right">
                                            Total
                                          </td>
                                          <td className="p-3 text-right">
                                            {formatCents(order.total, cur)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>

                                <div className="grid gap-6 md:grid-cols-3">
                                  {/* Customer Info */}
                                  <div>
                                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                      <User className="h-4 w-4" /> Customer
                                    </h4>
                                    <div className="bg-background space-y-1 rounded-lg border p-4 text-sm">
                                      <p className="font-medium">{order.customerName}</p>
                                      <p className="text-muted-foreground">{order.customerEmail}</p>
                                      {order.customerPhone && (
                                        <p className="text-muted-foreground">
                                          {order.customerPhone}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Shipping Address */}
                                  <div>
                                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                      <MapPin className="h-4 w-4" /> Shipping Address
                                    </h4>
                                    <div className="bg-background rounded-lg border p-4 text-sm">
                                      {order.shippingAddress ? (
                                        <div className="space-y-1">
                                          {order.shippingAddress.name && (
                                            <p className="font-medium">
                                              {order.shippingAddress.name}
                                            </p>
                                          )}
                                          {order.shippingAddress.line1 && (
                                            <p>{order.shippingAddress.line1}</p>
                                          )}
                                          {order.shippingAddress.line2 && (
                                            <p>{order.shippingAddress.line2}</p>
                                          )}
                                          <p>
                                            {[
                                              order.shippingAddress.city,
                                              order.shippingAddress.state,
                                              order.shippingAddress.postalCode,
                                            ]
                                              .filter(Boolean)
                                              .join(', ')}
                                          </p>
                                          {order.shippingAddress.country && (
                                            <p>{order.shippingAddress.country}</p>
                                          )}
                                          {order.shippingAddress.phone && (
                                            <p className="text-muted-foreground">
                                              {order.shippingAddress.phone}
                                            </p>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="text-muted-foreground">Not provided</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Payment & Status */}
                                  <div>
                                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                      <CreditCard className="h-4 w-4" /> Payment
                                    </h4>
                                    <div className="bg-background space-y-2 rounded-lg border p-4 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Method</span>
                                        <span className="capitalize">
                                          {order.payment?.method || '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Gateway</span>
                                        <span className="capitalize">
                                          {order.payment?.gateway || '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Status</span>
                                        {getPaymentStatusBadge(order.payment?.status)}
                                      </div>
                                      {order.payment?.paymentMode === 'test' && (
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Mode</span>
                                          <Badge
                                            variant="outline"
                                            className="border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                                          >
                                            <FlaskConical className="mr-1 h-3 w-3" />
                                            Test Mode
                                          </Badge>
                                        </div>
                                      )}
                                      {order.payment?.paidAt && (
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Paid at</span>
                                          <span>{formatDate(order.payment.paidAt)}</span>
                                        </div>
                                      )}
                                      {order.trackingNumber && (
                                        <div className="flex justify-between border-t pt-2">
                                          <span className="text-muted-foreground">Tracking</span>
                                          {order.trackingUrl ? (
                                            <a
                                              href={order.trackingUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-primary hover:underline"
                                            >
                                              {order.trackingNumber}
                                            </a>
                                          ) : (
                                            <span>{order.trackingNumber}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Customer Note */}
                                {order.customerNote && (
                                  <div className="bg-background rounded-lg border p-4">
                                    <h4 className="mb-1 text-sm font-semibold">Customer Note</h4>
                                    <p className="text-muted-foreground text-sm">
                                      {order.customerNote}
                                    </p>
                                  </div>
                                )}

                                {/* Order Status Update */}
                                {(() => {
                                  const nextStatuses = getNextStatuses(order.status);
                                  if (nextStatuses.length === 0) return null;
                                  return (
                                    <div className="bg-background rounded-lg border p-4">
                                      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <Truck className="h-4 w-4" /> Update Status
                                      </h4>
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className="text-muted-foreground">Current:</span>
                                          {getOrderStatusBadge(order.status)}
                                          <span className="text-muted-foreground mx-1">→</span>
                                        </div>
                                        <Select
                                          onValueChange={(value) => {
                                            updateStatusMutation.mutate({
                                              orderId: order._id,
                                              status: value as OrderStatus,
                                            });
                                          }}
                                        >
                                          <SelectTrigger className="h-9 w-[180px]">
                                            <SelectValue placeholder="Change to..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {nextStatuses.map((s) => (
                                              <SelectItem key={s} value={s}>
                                                {statusLabel(s)}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {updateStatusMutation.isPending && (
                                          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Showing {(meta.page - 1) * meta.limit + 1} to{' '}
            {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} orders
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page <= 1}
              onClick={() => setPage(meta.page - 1)}
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(meta.totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (meta.totalPages <= 5) {
                pageNum = i + 1;
              } else if (meta.page <= 3) {
                pageNum = i + 1;
              } else if (meta.page >= meta.totalPages - 2) {
                pageNum = meta.totalPages - 4 + i;
              } else {
                pageNum = meta.page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === meta.page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.totalPages}
              onClick={() => setPage(meta.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
